import axios from 'axios'
import { useAuthStore } from '@/stores/auth'

// Create axios instance
export const api = axios.create({
  baseURL: `${import.meta.env.VITE_SERVER_API_URL}/api`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    if (authStore.accessToken) {
      config.headers.Authorization = `Bearer ${authStore.accessToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config
    const authStore = useAuthStore()

    // If error is 401 and we haven't tried to refresh token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry if this is already a refresh token request
      if (originalRequest.url?.includes('/auth/refresh')) {
        // Refresh token failed, logout user immediately
        await authStore.logout()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        // Try to refresh token
        const success = await authStore.refreshAccessToken()
        
        if (success) {
          // Retry original request with new token
          return api(originalRequest)
        } else {
          // Refresh failed, logout user
          await authStore.logout()
          window.location.href = '/login'
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        await authStore.logout()
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// API response wrapper
export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  message?: string
  error?: string
}

// Error handler
export const handleApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message
  }
  
  if (error.response?.data?.error) {
    return error.response.data.error
  }
  
  if (error.message) {
    return error.message
  }
  
  return 'An unexpected error occurred'
}
